import {
  content_v1_DatasourcePreset,
  core_v1_DataSource,
} from '@antimatterhq/asl-api-ts-v1';
import { produce, WritableDraft } from 'immer';
import { getTemporaryFields } from './temporaryFields';
import {
  deepEqual,
  getCustomFunctionValues,
  getFieldSchemaValues,
  getJSONExtractValues,
  getMode,
  getPreserveUnreferencedColumnsValues,
  setCustomFunction,
  setFieldSchemaValues,
} from './';
import { GoldDataSourceSchemaValues } from '@/zod/schemas/dataSource/Gold';
import { filterNulls } from '@/utils/filterNulls';
import {
  Datasource_Gold_AddTable,
  Datasource_Gold_ModifyTable,
  Datasource_PresetItem,
  TransformRequest_Transform,
} from '@/types/api';
import {
  AddTableSchemaValues,
  ModifyTableSchemaValues,
} from '@/zod/schemas/dataSource/Common';

export const getModifyGoldTables = (
  modifyTables: Datasource_Gold_ModifyTable[],
  omitTables: string[],
): ModifyTableSchemaValues[] => {
  const tables = modifyTables.map((table) => {
    return {
      name: table.name || '',
      jsonExtract: getJSONExtractValues({}),
      ...getPreserveUnreferencedColumnsValues({}),
      ...getCustomFunctionValues(table.custom),
      filter: table.filter || '',
      postFilter: table.postFilter || '',
      omitFields: table.omitFields || [],
      addFields: getFieldSchemaValues(filterNulls(table.addFields)),
      sourceTable: table.sourceTable ?? '',
      tableMode: getMode(table.name || '', table.custom, omitTables),
      enableOverrideLiquidColumns: false,
      overrideLiquidColumns: [''],
      ...getTemporaryFields({}),
    };
  });

  return tables;
};

export const setModifyGoldTables = (
  modifyTablesValues: ModifyTableSchemaValues[],
): Datasource_Gold_ModifyTable[] => {
  return modifyTablesValues.map((table) => {
    const configure = table.tableMode === 'configure';
    const custom = table.tableMode === 'custom';

    if (!table.sourceTable) {
      throw new Error('sourceTable is required');
    }

    const modifyTable: Datasource_Gold_ModifyTable = {
      name: table.name || '',
      custom: custom ? setCustomFunction(table) : undefined,
      filter: configure ? table.filter || undefined : undefined,
      postFilter: configure ? table.postFilter || undefined : undefined,
      omitFields: configure ? table.omitFields || undefined : undefined,
      addFields: configure ? setFieldSchemaValues(table.addFields) : undefined,
      sourceTable: table.sourceTable,
    };
    return modifyTable;
  });
};

export const getAddGoldTables = (
  addTables: Datasource_Gold_AddTable[] | undefined,
): AddTableSchemaValues[] => {
  return (addTables ?? []).map((table) => ({
    name: table.name ?? '',
    jsonExtract: getJSONExtractValues({}),
    ...getPreserveUnreferencedColumnsValues({}),
    ...getCustomFunctionValues(table.custom),
    filter: table.filter ?? '',
    postFilter: table.postFilter ?? '',
    fields: getFieldSchemaValues(filterNulls(table.fields)),
    enableOverrideLiquidColumns: false,
    overrideLiquidColumns: [],
    sourceTable: table.sourceTable ?? '',
    tableMode: getMode(table.name || '', table.custom, []),
    ...getTemporaryFields({}),
  }));
};

export const setAddGoldTables = (
  addTablesValues: AddTableSchemaValues[],
): NonNullable<Datasource_Gold_AddTable>[] | undefined => {
  if (!addTablesValues.length) {
    return undefined;
  }
  return addTablesValues.map((table) => {
    const configure = table.tableMode === 'configure';
    const custom = table.tableMode === 'custom';

    if (!table.sourceTable) {
      throw new Error('sourceTable is required');
    }

    const addTable: Datasource_Gold_AddTable = {
      name: table.name,
      custom: custom ? setCustomFunction(table) : undefined,
      filter: configure ? table.filter || undefined : undefined,
      postFilter: configure ? table.postFilter || undefined : undefined,
      fields: configure ? setFieldSchemaValues(table.fields) : undefined,
      sourceTable: table.sourceTable,
    };

    return addTable;
  });
};

export const getGoldInitialValues = (
  dataSource: core_v1_DataSource,
): GoldDataSourceSchemaValues => {
  return {
    addTables: getAddGoldTables(
      dataSource.spec.gold?.presetOverrides?.addTables ?? [],
    ),
    modifyTables: getModifyGoldTables(
      filterNulls(dataSource.spec.gold?.presetOverrides?.modifyTables),
      dataSource.spec.gold?.presetOverrides?.omitTables ?? [],
    ),
    customNotebookPath: dataSource.spec.custom?.notebook ?? '',
  };
};

export const getIsGoldCompleted = (dataSource: core_v1_DataSource) => {
  return !!dataSource;
};

export const setGoldFormValues = (
  baseState: core_v1_DataSource,
  values: GoldDataSourceSchemaValues,
) => {
  const omitTables = values.modifyTables
    .filter((table) => table.tableMode === 'disable')
    .map((table) => table.name);
  return produce(baseState, (draftState: WritableDraft<core_v1_DataSource>) => {
    draftState.spec.gold = {
      presetOverrides: {
        addTables: setAddGoldTables(values.addTables),
        modifyTables: setModifyGoldTables(values.modifyTables),
        omitTables: omitTables.length ? omitTables : undefined,
      },
    };
    draftState.spec.custom = values.customNotebookPath
      ? {
          notebook: values.customNotebookPath,
        }
      : undefined;
  });
};

// TODO this should be a gold specific preset
const mapGoldModifyTablePresetToDatasource = (
  presetTransform: Datasource_PresetItem,
  datasource: core_v1_DataSource,
): Datasource_Gold_ModifyTable => {
  const modifyTables = datasource.spec.gold?.presetOverrides?.modifyTables;

  const modifyTable = modifyTables?.find(
    (table) => table.name === presetTransform.name,
  );

  if (!modifyTable) {
    return {
      filter: presetTransform.filter || undefined,
      postFilter: presetTransform.postFilter || undefined,
      name: presetTransform.name || null,
      sourceTable: presetTransform.input || null,
    };
  }

  if (presetTransform.filter) {
    modifyTable.filter = presetTransform.filter;
  }

  if (presetTransform.postFilter) {
    modifyTable.postFilter = presetTransform.postFilter;
  }

  if (presetTransform.input) {
    modifyTable.sourceTable = presetTransform.input;
  }

  return modifyTable;
};

export const mapGoldPresetsToDataSource = (
  datasource: core_v1_DataSource,
  preset?: content_v1_DatasourcePreset,
) => {
  const transforms = filterNulls(preset?.gold);

  return produce(
    datasource,
    (draftState: WritableDraft<core_v1_DataSource>) => {
      draftState.spec.gold = {
        presetOverrides: {
          addTables: draftState.spec.gold?.presetOverrides?.addTables?.length
            ? draftState.spec.gold?.presetOverrides?.addTables
            : undefined,
          modifyTables: transforms?.length
            ? transforms
                ?.map((transform) =>
                  mapGoldModifyTablePresetToDatasource(transform, draftState),
                )
                .filter((table) =>
                  transforms.some((t) => t.name === table.name),
                )
            : undefined,
          omitTables: draftState.spec.gold?.presetOverrides?.omitTables?.length
            ? draftState.spec.gold?.presetOverrides?.omitTables
            : undefined,
        },
      };
    },
  );
};

export const goldHasUnsavedChanges = (
  datasource: core_v1_DataSource,
  unsavedDatasource: core_v1_DataSource | undefined,
) => {
  if (!unsavedDatasource) {
    return false;
  }
  return !deepEqual(datasource.spec.gold, unsavedDatasource.spec.gold);
};

export const mapGoldModifyTableToTransformRequest = (
  table: Datasource_Gold_ModifyTable,
): TransformRequest_Transform => {
  return {
    transformType: 'Gold',
    usePresetTable: table?.name || undefined,
    filter: table?.filter || undefined,
    postFilter: table?.postFilter || undefined,
    addFields: table?.addFields || undefined,
    presetOverrides: table?.omitFields
      ? { omitFields: table?.omitFields }
      : undefined,
  };
};

export const mapGoldAddTableToTransformRequest = (
  table: Datasource_Gold_AddTable,
): TransformRequest_Transform => {
  return {
    transformType: 'Gold',
    usePresetTable: table?.name || undefined,
    filter: table?.filter || undefined,
    postFilter: table?.postFilter || undefined,
    addFields: table?.fields || undefined,
  };
};

export const mapGoldTableToTransformRequest = (
  table: Datasource_Gold_AddTable,
  isModifyTable: boolean,
) => {
  if (isModifyTable) {
    return mapGoldModifyTableToTransformRequest(table);
  }
  return mapGoldAddTableToTransformRequest(table);
};
